import { useQuery } from '@tanstack/react-query';
import {
  Loader2,
  Users,
  Building2,
  Phone,
  AlertCircle,
  Scale,
  Landmark,
  Fingerprint,
  Stamp,
} from 'lucide-react';
import { fetchAssociationPublic } from '../services/api';
import { usePersonnalisation } from '../hooks/usePersonnalisation';
import { avecArticle } from '../utils/nomAssociation';

export default function Association() {
  // Le nom vient des reglages de l'instance. « avecArticle » choisit
  // l'article qui va avec — « le Comite des Fetes », « l'Amicale », «
  // l'association Racing Club » — et rend « notre association » quand rien
  // n'est encore regle, ce qui est vrai le premier jour.
  const perso = usePersonnalisation();
  const avecSonArticle = avecArticle(perso?.nomAssociation);
  const nomAssoc = avecSonArticle.charAt(0).toUpperCase() + avecSonArticle.slice(1);
  const query = useQuery({
    queryKey: ['association-public'],
    queryFn: fetchAssociationPublic,
  });

  if (query.isPending) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <p>Impossible de charger l'organigramme de l'association.</p>
        </div>
      </div>
    );
  }

  const bureau = query.data?.bureau ?? [];
  const benevoles = query.data?.benevoles ?? [];

  return (
    <div className="p-4 lg:p-0 space-y-6 lg:space-y-8 pb-24 lg:pb-0">
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">L'association</h1>
        {/*
          Le nom de l'association vient de ses reglages, pas du code.

          Cette phrase nommait un vrai client en dur.
          Elle s'affichait donc sur CHAQUE instance : un club de tennis lisait
          le nom d'un comite des fetes de l'Herault sur sa propre page, et
          n'avait aucun moyen de le corriger — ce n'etait pas un reglage,
          c'etait une ligne de code.
        */}
        <p className="mt-2 text-sm lg:text-base text-gray-600 max-w-3xl dark:text-gray-400">
          {nomAssoc}, une association loi 1901. Découvrez le bureau qui l'anime et les
          bénévoles qui la font vivre.
        </p>
      </div>

      {/* Organigramme du bureau */}
      <section className="space-y-3">
        <h2 className="text-lg lg:text-xl font-semibold text-gray-900 flex items-center gap-2 dark:text-gray-100">
          <Building2 className="w-5 h-5 text-primary-600" /> Le bureau
        </h2>
        {bureau.length === 0 ? (
          <p className="text-gray-500 text-sm dark:text-gray-400">Le bureau n'a pas encore été constitué.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
            {bureau.map((m: any) => (
              <div key={m.id} className="card p-4 flex items-center gap-3">
                <Avatar membre={m} />
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 truncate dark:text-gray-100">{m.fullName}</p>
                  <p className="text-sm text-primary-700 font-medium">{m.bureauRole?.label ?? 'Membre du bureau'}</p>
                  {m.telephone && (
                    <p className="text-xs text-gray-500 flex items-center gap-1 dark:text-gray-400">
                      <Phone className="w-3 h-3" /> {m.telephone}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Bénévoles */}
      <section className="space-y-3">
        <h2 className="text-lg lg:text-xl font-semibold text-gray-900 flex items-center gap-2 dark:text-gray-100">
          <Users className="w-5 h-5 text-primary-600" /> Les bénévoles
        </h2>
        {benevoles.length === 0 ? (
          <p className="text-gray-500 text-sm dark:text-gray-400">Aucun bénévole inscrit pour le moment.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
            {benevoles.map((m: any) => (
              <div key={m.id} className="card p-4 flex items-center gap-3">
                <Avatar membre={m} />
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 truncate dark:text-gray-100">{m.fullName}</p>
                  {m.telephone && (
                    <p className="text-xs text-gray-500 flex items-center gap-1 dark:text-gray-400">
                      <Phone className="w-3 h-3" /> {m.telephone}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Identité légale */}
      <MentionsLegales legal={query.data?.legal} />
    </div>
  );
}

/**
 * Identité légale de l'association, en pied de page.
 *
 * Ces informations sont déjà publiques — le Journal officiel des associations
 * et l'annuaire des entreprises les diffusent. Les republier ici évite au
 * visiteur d'aller les chercher, et vaut mention légale pour un site
 * associatif.
 */
function MentionsLegales({ legal }: { legal?: Record<string, string | null> }) {
  if (!legal) return null;

  const identite = [
    ['Dénomination', legal.raisonSociale],
    ['Forme juridique', legal.formeJuridique],
    ['Siège social', [legal.adresse, legal.codePostalVille].filter(Boolean).join(', ')],
    ['Département', legal.departement],
  ] as const;

  const immatriculation = [
    ['SIRET', legal.siret],
    ['SIREN', legal.siren],
    ['N° RNA', legal.rna],
    ['Code NAF / APE', legal.codeNaf],
  ] as const;

  const declaration = [
    ['Déclarée le', legal.dateDeclaration],
    ['Auprès de', legal.lieuDeclaration],
    [
      'Journal officiel',
      legal.journalOfficielParution
        ? `parution n° ${legal.journalOfficielParution}${
            legal.journalOfficielAnnonce ? `, annonce n° ${legal.journalOfficielAnnonce}` : ''
          }`
        : null,
    ],
  ] as const;

  return (
    <footer className="pt-6 border-t border-gray-200 dark:border-gray-800 space-y-4">
      <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-2">
        <Scale className="w-4 h-4 text-gray-400" />
        Mentions légales
      </h2>

      {legal.objet && (
        <div className="rounded-2xl bg-gradient-to-br from-primary-600 to-primary-800 text-white p-5 lg:p-6">
          <p className="text-xs uppercase tracking-wide text-primary-100">Objet déclaré</p>
          <p className="mt-1 lg:text-lg font-medium leading-snug first-letter:uppercase">
            {legal.objet}
          </p>
        </div>
      )}

      {/* Trois cartes plutôt qu'un pied de page en petits caractères : ces
          informations sont l'identité de l'association, pas une formalité
          qu'on relègue. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
        <CarteLegale titre="Identité" icone={Landmark} teinte="text-primary-600" lignes={identite} />
        <CarteLegale titre="Immatriculation" icone={Fingerprint} teinte="text-amber-600" lignes={immatriculation} />
        <CarteLegale titre="Déclaration" icone={Stamp} teinte="text-secondary-600" lignes={declaration} />
      </div>

      <p className="text-xs text-gray-400 dark:text-gray-500">
        {legal.directeurPublication && <>Directeur de publication : {legal.directeurPublication}. </>}
        {legal.hebergeur && <>Hébergement : {legal.hebergeur}. </>}
        {legal.contactRgpd && (
          <>
            Données personnelles :{' '}
            <a href={`mailto:${legal.contactRgpd}`} className="underline hover:text-primary-600">
              {legal.contactRgpd}
            </a>
            .
          </>
        )}
      </p>
    </footer>
  );
}

/**
 * Une facette de l'identité légale, présentée en carte.
 *
 * Une carte vide ne s'affiche pas : mieux vaut deux cartes pleines que trois
 * dont une annonce un titre sans rien dessous.
 */
function CarteLegale({
  titre,
  icone: Icone,
  teinte,
  lignes,
}: {
  titre: string;
  icone: typeof Landmark;
  teinte: string;
  lignes: ReadonlyArray<readonly [string, string | null | undefined]>;
}) {
  const remplies = lignes.filter(([, valeur]) => valeur);
  if (remplies.length === 0) return null;

  return (
    <div className="card lg:p-5 space-y-3">
      <div className="flex items-center gap-2">
        <span className={`p-2 rounded-xl bg-gray-100 dark:bg-gray-800 ${teinte}`}>
          <Icone className="w-4 h-4" />
        </span>
        <p className="font-semibold text-gray-900 dark:text-gray-100">{titre}</p>
      </div>

      <dl className="text-sm space-y-1.5">
        {remplies.map(([label, valeur]) => (
          <div key={label}>
            <dt className="text-xs text-gray-500 dark:text-gray-400">{label}</dt>
            <dd className="text-gray-900 dark:text-gray-100 font-medium break-words">{valeur}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Avatar({ membre }: { membre: any }) {
  if (membre.photoUrl) {
    return (
      <img
        src={membre.photoUrl}
        alt={membre.fullName}
        className="w-12 h-12 rounded-full object-cover bg-gray-100 shrink-0"
      />
    );
  }
  return (
    <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold shrink-0">
      {membre.fullName?.charAt(0) || '?'}
    </div>
  );
}
